import { TimeZone } from 'basics/enums/timeZoneList.enums';
import { SelectOption } from 'basics/options/options.types';
import { getUTCDate } from 'basics/utils/dates/dates.utils';
import { AgendaEntry, AssetTarget, HostProvider } from 'generated/event.types';
import { commonFields } from 'modules/events/basics/constants/commonFormFields.constants';
import { EventPromotionalityEnum } from 'modules/events/basics/enums/events.enums';
import { AssetsSentToDB } from 'modules/events/basics/types/eventData.types';
import { Speaker } from 'modules/events/basics/types/speaker.types';
import { YodaFieldValue } from 'yoda-ui/yodaForm/yodaForm.types';

export const transformInputAssets = (data: YodaFieldValue) => {
  const assetsFields = [
    commonFields.cardImage,
    commonFields.programLogo,
    commonFields.teaser,
    commonFields.agendaFile,
  ];

  const initialAcc: AssetsSentToDB = {};

  const assets = assetsFields.reduce((acc, current) => {
    const target = current.split('.').at(-1) as AssetTarget;
    if (data[current]) {
      acc[target] = data[current]?.map((item: { id: string }) => item.id);
    }
    return acc;
  }, initialAcc);
  return Object.keys(assets).length ? { assets } : {};
};

export const transformInputProvider = (data: YodaFieldValue) => {
  const hostProvider = data[commonFields.hostProvider];
  const hostProviderLink = data[commonFields.hostProviderLink];
  const brightcoveProviderId = data[commonFields.brightcoveId];

  return {
    provider: {
      host: hostProvider,
      ...([HostProvider.other, HostProvider.teams].includes(hostProvider) ? { participantUrl: hostProviderLink } : {}),
      ...([HostProvider.other, HostProvider.teams].includes(hostProvider) ? { organizerUrl: hostProviderLink } : {}),
      ...(hostProvider === HostProvider.brightcove ? { brightcoveId: brightcoveProviderId } : {}),
    },
  };
};

export const transformInputSession = (data: YodaFieldValue) => {
  const dateRange = data[commonFields.sessionDateRange];
  const startTime = data[commonFields.sessionStartTime];
  const endTime = data[commonFields.sessionEndTime];
  const timeZone = data[commonFields.sessionTimezone];

  if (timeZone && endTime && startTime && dateRange) {
    const [startDate, endDate] = dateRange;
    return {
      session: {
        startDate: getUTCDate(startDate || undefined, startTime, timeZone) || new Date(),
        endDate: getUTCDate(endDate || undefined, endTime, timeZone) || new Date(),
        timeZone: TimeZone.UTC,
      },
    };
  }
  return undefined;
};

export const transformInputBiogenLincConfig = (dataToSave: YodaFieldValue, formValues: YodaFieldValue) => {
  const {
    isVisibleOnBiogenLinc,
    isPublic,
    prcCodeEventDetails,
    showSendInvitations,
    enableGuestRegistration,
    enableGuestRegistrationCountryDropdown,
    checkConsent,
    checkGuestTextMessageConsent,
    collectTextMessageUserConsent,
    fullForm,
    shouldVeevaNetworkMaster,
    isSwitzerlandSpecific,
  } = commonFields;

  const { public: publicEnum } = EventPromotionalityEnum;
  const isVisibleOnBiogenLincCurrent = formValues[isVisibleOnBiogenLinc];
  const showSendInvitationsCurrent = formValues[showSendInvitations];
  const isSwitzerlandSpecificCurrent = formValues[isSwitzerlandSpecific];

  const isVisibleOnBiogenLincHasChanged = dataToSave[isVisibleOnBiogenLinc] !== undefined;
  const isPublisHasChanged = dataToSave[isPublic] !== undefined;
  const prcCodeEventDetailsHasChanged = dataToSave[prcCodeEventDetails] !== undefined;
  const showSendInvitationsHasChanged = dataToSave[showSendInvitations] !== undefined;
  const enableGuestRegistrationHasChanged = dataToSave[enableGuestRegistration] !== undefined;
  const enableGuestRegistrationCountryDropdownHasChanged = dataToSave[enableGuestRegistrationCountryDropdown] !== undefined;
  const checkConsentHasChanged = dataToSave[checkConsent] !== undefined;
  const checkGuestTextMessageConsentHasChanged = dataToSave[checkGuestTextMessageConsent] !== undefined;
  const collectTextMessageUserConsentHasChanged = dataToSave[collectTextMessageUserConsent] !== undefined;
  const fullFormHasChanged = dataToSave[fullForm] !== undefined;
  const shouldVeevaNetworkMasterChanged = dataToSave[shouldVeevaNetworkMaster] !== undefined;
  const isSwitzerlandSpecificHasChanged = dataToSave[isSwitzerlandSpecific] !== undefined;
  const biogenLincConfigHasChanged = isVisibleOnBiogenLincHasChanged
    || isPublisHasChanged
    || prcCodeEventDetailsHasChanged
    || enableGuestRegistrationHasChanged
    || enableGuestRegistrationCountryDropdownHasChanged
    || showSendInvitationsHasChanged
    || isSwitzerlandSpecificHasChanged
    || checkConsentHasChanged
    || checkGuestTextMessageConsentHasChanged
    || collectTextMessageUserConsentHasChanged
    || fullFormHasChanged
    || shouldVeevaNetworkMasterChanged;

  const enableGuestRegistrationToSave = formValues[enableGuestRegistration] || false;
  const enableGuestRegistrationCountryDropdownToSave = formValues[enableGuestRegistrationCountryDropdown] || false;
  const checkConsentHasChangedToSave = formValues[checkConsent] || false;
  // checkGuestTextMessageConsent is synced with collectTextMessageUserConsent
  const checkGuestTextMessageConsentHasChangedToSave = formValues[collectTextMessageUserConsent] || false;
  const fullFormHasChangedToSave = formValues[fullForm] || false;
  const shouldVeevaNetworkMasterToSave = formValues[shouldVeevaNetworkMaster] ?? true;

  const prcCodeEventDetailsToSave = prcCodeEventDetailsHasChanged
    ? dataToSave[prcCodeEventDetails]
    : formValues[prcCodeEventDetails];

  if (!biogenLincConfigHasChanged) {
    return {};
  }

  if (!isVisibleOnBiogenLincCurrent) {
    return {
      biogenLincConfig: {
        isVisibleOnBiogenLinc: false,
        isPublic: false,
        enableGuestRegistration: false,
        checkConsent: false,
        checkGuestTextMessageConsent: false,
        fullForm: false,
        prcCodeEventDetails: prcCodeEventDetailsToSave,
        showSendInvitations: showSendInvitationsCurrent,
        isSwitzerlandSpecific: isSwitzerlandSpecificCurrent,
      },
    };
  }

  return {
    biogenLincConfig: {
      isVisibleOnBiogenLinc: true,
      isPublic: isPublisHasChanged ? dataToSave[isPublic] === publicEnum : formValues[isPublic] === publicEnum,
      enableGuestRegistration: enableGuestRegistrationToSave,
      enableGuestRegistrationCountryDropdown: enableGuestRegistrationCountryDropdownToSave,
      checkConsent: checkConsentHasChangedToSave,
      checkGuestTextMessageConsent: checkGuestTextMessageConsentHasChangedToSave,
      fullForm: fullFormHasChangedToSave,
      shouldVeevaNetworkMaster: shouldVeevaNetworkMasterToSave,
      prcCodeEventDetails: prcCodeEventDetailsToSave,
      showSendInvitations: showSendInvitationsCurrent,
      isSwitzerlandSpecific: isSwitzerlandSpecificCurrent,
    },
  };
};

export const transformCventConfig = (data: YodaFieldValue) => {
  const title = data[commonFields.cventTitle];
  const sourceUrl = data[commonFields.cventSourceUrl];

  if (title !== '' && sourceUrl !== '') {
    return {
      cventConfig: {
        title,
        sourceUrl,
      },
    };
  }

  return { cventConfig: null };
};

export const transformSpeakers = (data: YodaFieldValue) => {
  const speakers = data[commonFields.speakers];
  if (speakers) {
    const transformation = speakers.map((speaker: Speaker) => speaker.id);
    return {
      speakers: transformation,
    };
  }
  return {};
};

export const transformAgenda = (data: YodaFieldValue) => {
  const agenda = data[commonFields.agenda];
  return (agenda && agenda.length > 0)
    ? data[commonFields.agenda].map((agendaEntry: AgendaEntry) => {
      const { title, description, session } = agendaEntry;
      session.timeZone = TimeZone.UTC;
      return {
        title,
        description,
        session,
      };
    }) : null;
};

export const transformUserGroups = (data: YodaFieldValue) => {
  const userGroups = data[commonFields.userGroups];
  return (userGroups && userGroups.length > 0)
    ? userGroups.map((group: SelectOption | string) => (typeof group === 'string' ? group : group.id)) : null;
};
